import { Fragment, useId } from 'react';
import { STATUS_CLASSES, STATUS_CLASS_CONFIG, visibleStatusClasses } from '@/config/httpStatus';
import { useElementWidth } from '@/hooks/useElementWidth';
import { niceTicks, pickDayStartIndices, pickTickIndices } from '@/utils/chart';
import { formatAxisTime, formatShortDateTime } from '@/utils/date';
import { formatCompactNumber, formatInterval, formatNumber } from '@/utils/format';
import type { HttpStatsPoint, StatusClass, StatusClassCounts } from '@/types/stats';
import { StatusClassLegend } from './StatusClassLegend';
import './stats.css';

export interface StatusTimeChartProps {
  /** Gap-filled buckets in time order */
  points: HttpStatsPoint[];
  /** Bucket width in seconds */
  intervalSeconds: number;
  /** Visible title and accessible name */
  title: string;
}

const HEIGHT = 240;
const MARGIN = { top: 12, right: 12, bottom: 28, left: 48 };
/** Width used until the container is measured (and in tests) */
const FALLBACK_WIDTH = 720;
const MAX_BAR_WIDTH = 24;
/** Surface gap between stacked segments */
const SEGMENT_GAP = 2;
const CORNER_RADIUS = 4;
/** Minimum horizontal room per time label */
const MIN_LABEL_SPACING = 90;

interface Segment {
  cls: StatusClass;
  value: number;
  y: number;
  height: number;
}

/**
 * Stack the non-empty classes of a bucket upwards from the baseline, server
 * errors first. Segments are separated by a surface gap; a non-zero segment
 * is at least 1px tall so it never disappears.
 */
function stackSegments(counts: StatusClassCounts, pxPerRequest: number, baseline: number): Segment[] {
  const segments: Segment[] = [];
  let cumulative = 0;

  for (const cls of STATUS_CLASSES) {
    const value = counts[cls] ?? 0;
    if (value <= 0) continue;

    const bottom = baseline - cumulative * pxPerRequest - (segments.length > 0 ? SEGMENT_GAP : 0);
    cumulative += value;
    const top = baseline - cumulative * pxPerRequest;
    const height = Math.max(1, bottom - top);
    segments.push({ cls, value, y: bottom - height, height });
  }

  return segments;
}

function sumClasses(points: HttpStatsPoint[]): StatusClassCounts {
  const totals: StatusClassCounts = { '1xx': 0, '2xx': 0, '3xx': 0, '4xx': 0, '5xx': 0 };
  for (const point of points) {
    for (const cls of STATUS_CLASSES) {
      totals[cls] += point.status_classes[cls] ?? 0;
    }
  }
  return totals;
}

/**
 * Hover/tooltip text of one bucket
 */
function describeBucket(point: HttpStatsPoint, interval: string): string {
  const heading = `${formatShortDateTime(point.t)} (${interval})`;
  if (point.requests <= 0) return `${heading}\nNo requests`;

  const lines = STATUS_CLASSES.filter((cls) => (point.status_classes[cls] ?? 0) > 0).map(
    (cls) => `${STATUS_CLASS_CONFIG[cls].label}: ${formatNumber(point.status_classes[cls])}`
  );
  return [heading, `${formatNumber(point.requests)} requests`, ...lines].join('\n');
}

/**
 * Stacked column chart of requests per time bucket by status class.
 *
 * One column per bucket (empty buckets keep their slot), a hairline grid with
 * a compact request scale, a legend, a tooltip per column and a data-table
 * twin for keyboard and screen-reader users.
 */
export function StatusTimeChart({ points, intervalSeconds, title }: StatusTimeChartProps) {
  const [containerRef, width] = useElementWidth<HTMLElement>(FALLBACK_WIDTH);
  const titleId = useId();
  const descId = useId();

  const totals = sumClasses(points);
  const classes = visibleStatusClasses(totals);
  const interval = formatInterval(intervalSeconds);

  if (points.length === 0) {
    return (
      <figure ref={containerRef} className="status-chart">
        <figcaption className="status-chart-title">{title}</figcaption>
        <p className="status-chart-empty">No data for this period.</p>
      </figure>
    );
  }

  const plotLeft = MARGIN.left;
  const plotWidth = Math.max(1, width - MARGIN.left - MARGIN.right);
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const baseline = MARGIN.top + plotHeight;

  const peak = points.reduce((best, point) => (point.requests > best.requests ? point : best), points[0]);
  const ticks = niceTicks(peak.requests);
  const scaleMax = ticks[ticks.length - 1];
  const pxPerRequest = plotHeight / scaleMax;
  const yOf = (value: number) => baseline - value * pxPerRequest;

  const band = plotWidth / points.length;
  const barGap = band >= 6 ? 2 : band >= 3 ? 1 : 0;
  const barWidth = Math.max(1, Math.min(MAX_BAR_WIDTH, band - barGap));
  // Sub-hour buckets get time labels; longer ones a label per day
  const maxLabels = Math.max(2, Math.floor(plotWidth / MIN_LABEL_SPACING));
  const labelIndices =
    intervalSeconds < 3600
      ? pickTickIndices(points.length, maxLabels)
      : pickDayStartIndices(points.map((point) => point.t), maxLabels);

  const presentTotals = classes
    .filter((cls) => totals[cls] > 0)
    .map((cls) => `${cls} ${formatNumber(totals[cls])}`)
    .join(', ');
  const description =
    `${points.length} columns, one per ${interval}, from ${formatShortDateTime(points[0].t)} ` +
    `to ${formatShortDateTime(points[points.length - 1].t)}. ` +
    (peak.requests > 0
      ? `Peak ${formatNumber(peak.requests)} requests at ${formatShortDateTime(peak.t)}. Totals: ${presentTotals}.`
      : 'No requests.');

  const nonEmptyPoints = points.filter((point) => point.requests > 0);

  return (
    <figure ref={containerRef} className="status-chart">
      <figcaption className="status-chart-header">
        <span className="status-chart-title">{title}</span>
        <StatusClassLegend classes={classes} />
      </figcaption>

      <div className="status-chart-plot">
        <svg
          width={width}
          height={HEIGHT}
          viewBox={`0 0 ${width} ${HEIGHT}`}
          role="img"
          aria-labelledby={`${titleId} ${descId}`}
        >
          <title id={titleId}>{title}</title>
          <desc id={descId}>{description}</desc>

          {/* Scale */}
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                className={tick === 0 ? 'status-chart-baseline' : 'status-chart-grid'}
                x1={plotLeft}
                x2={plotLeft + plotWidth}
                y1={yOf(tick)}
                y2={yOf(tick)}
              />
              <text className="status-chart-label" x={plotLeft - 8} y={yOf(tick)} dy="0.32em" textAnchor="end">
                {formatCompactNumber(tick)}
              </text>
            </g>
          ))}

          {/* One column per bucket; the whole band is the hover target */}
          {points.map((point, index) => {
            const bandLeft = plotLeft + index * band;
            const x = bandLeft + (band - barWidth) / 2;
            const segments = stackSegments(point.status_classes, pxPerRequest, baseline);

            return (
              <g key={point.t} className="status-chart-column" data-testid="status-chart-column">
                <title>{describeBucket(point, interval)}</title>
                <rect className="status-chart-hit" x={bandLeft} y={MARGIN.top} width={band} height={plotHeight} />
                {segments.map((segment, segmentIndex) => {
                  const isTop = segmentIndex === segments.length - 1;
                  const radius = isTop && barWidth >= 8 ? Math.min(CORNER_RADIUS, segment.height / 2) : 0;
                  const className = STATUS_CLASS_CONFIG[segment.cls].className;
                  return (
                    <Fragment key={segment.cls}>
                      <rect
                        className={`status-chart-bar ${className}`}
                        data-status-class={segment.cls}
                        data-value={segment.value}
                        x={x}
                        y={segment.y}
                        width={barWidth}
                        height={segment.height}
                        rx={radius || undefined}
                      />
                      {/* Square off the bottom corners: only the data end is rounded */}
                      {radius > 0 && (
                        <rect
                          className={className}
                          x={x}
                          y={segment.y + segment.height - radius}
                          width={barWidth}
                          height={radius}
                        />
                      )}
                    </Fragment>
                  );
                })}
              </g>
            );
          })}

          {/* Time labels */}
          {labelIndices.map((index) => (
            <text
              key={points[index].t}
              className="status-chart-label"
              x={plotLeft + (index + 0.5) * band}
              y={HEIGHT - 8}
              textAnchor="middle"
            >
              {formatAxisTime(points[index].t, intervalSeconds)}
            </text>
          ))}
        </svg>
      </div>

      <details className="status-chart-data">
        <summary>Show data table</summary>
        {nonEmptyPoints.length === 0 ? (
          <p className="status-chart-empty">No requests in this period.</p>
        ) : (
          <div className="status-chart-data-scroll">
            <table className="status-chart-data-table">
              <caption className="visually-hidden">{title}</caption>
              <thead>
                <tr>
                  <th scope="col">Time</th>
                  <th scope="col">Requests</th>
                  {classes.map((cls) => (
                    <th key={cls} scope="col">
                      {cls}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {nonEmptyPoints.map((point) => (
                  <tr key={point.t}>
                    <th scope="row">{formatShortDateTime(point.t)}</th>
                    <td>{formatNumber(point.requests)}</td>
                    {classes.map((cls) => (
                      <td key={cls}>{formatNumber(point.status_classes[cls] ?? 0)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </details>
    </figure>
  );
}
